
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2>{`TypeScript`}</h2>
    <p>{`styled-components has community-organized `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/@types/styled-components"
      }}>{`TypeScript definitions`}</a>{` on `}<a parentName="p" {...{
        "href": "https://github.com/DefinitelyTyped/DefinitelyTyped"
      }}>{`DefinitelyTyped`}</a>{` which powers the editing experience in IDEs and can provide types for TypeScript projects. To install them, run:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`# Web
npm install --save-dev @types/styled-components

# React Native
npm install --save-dev @types/styled-components @types/styled-components-react-native
`}</code></pre>
    <p>{`React Native only: If your `}<inlineCode parentName="p">{`tsconfig`}</inlineCode>{` assigns `}<inlineCode parentName="p">{`types`}</inlineCode>{` then you will need to add "styled-components-react-native" there.  For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"types": ["jest", "styled-components-react-native"],
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`Now that Babel 7 is out and the `}<a parentName="p" {...{
          "href": "https://babeljs.io/docs/en/babel-preset-typescript"
        }}>{`TypeScript preset`}</a>{` is available, it's now possible to use the `}<a parentName="p" {...{
          "href": "/docs/tooling#babel-plugin"
        }}>{`styled-components babel plugin`}</a>{` in conjunction with TypeScript.`}</p>
    </blockquote>
    <p>{`Before you can effectively start to use TypeScript you will have to do a little bit of configuration.`}</p>
    <h3>{`Create a declarations file`}</h3>
    <p>{`TypeScript definitions for styled-components can be extended by using `}<a parentName="p" {...{
        "href": "https://www.typescriptlang.org/docs/handbook/declaration-merging.html"
      }}>{`declaration merging`}</a>{` since version `}<inlineCode parentName="p">{`v4.1.4`}</inlineCode>{` of the definitions.`}</p>
    <p>{`So the first step is creating a declarations file. Let's name it `}<inlineCode parentName="p">{`styled.d.ts`}</inlineCode>{` for example.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`// import original module declarations
import 'styled-components';

// and extend them!
declare module 'styled-components' {
  export interface DefaultTheme {
    borderRadius: string;

    colors: {
      main: string;
      secondary: string;
    };
  }
}
`}</code></pre>
    <p>{`React-Native:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`declare module 'styled-components/native' {
  export interface DefaultTheme {
    borderRadius: string;

    colors: {
      main: string;
      secondary: string;
    };
  }
}
`}</code></pre>
    <p><inlineCode parentName="p">{`DefaultTheme`}</inlineCode>{` is being used as an interface of `}<inlineCode parentName="p">{`props.theme`}</inlineCode>{` out of the box. By default the interface `}<inlineCode parentName="p">{`DefaultTheme`}</inlineCode>{` is empty so that's why we need to extend it.`}</p>
    <h3>{`Create a theme`}</h3>
    <p>{`Now we can create a theme just by using the `}<inlineCode parentName="p">{`DefaultTheme`}</inlineCode>{` declared at the step above.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`// my-theme.ts
import { DefaultTheme } from 'styled-components';

const myTheme: DefaultTheme = {
  borderRadius: '5px',

  colors: {
    main: 'cyan',
    secondary: 'magenta',
  },
};

export { myTheme };
`}</code></pre>
    <p>{`React-Native:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// styled-components.ts
import * as styledComponents from "styled-components/native";

import ThemeInterface from "./theme";

const {
  default: styled,
  css,
  ThemeProvider
} = styledComponents as styledComponents.ReactNativeThemedStyledComponentsModule<ThemeInterface>;

export { css, ThemeProvider };
export default styled;
`}</code></pre>
    <h3>{`Styling components`}</h3>
    <p>{`That's it! We're able to use styled-components just by using any original import.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import styled, { createGlobalStyle, css } from 'styled-components';

// theme is now fully typed
export const MyComponent = styled.div\`
  color: \${props => props.theme.colors.main};
\`;

// theme is also fully typed
export MyGlobalStyle = createGlobalStyle\`
  body {
    background-color: \${props => props.theme.colors.secondary};
  }
\`;

// and this theme is fully typed as well
export cssHelper = css\`
  border: 1px solid \${props => props.theme.borderRadius};
\`;
`}</code></pre>
    <h3>{`Using custom props`}</h3>
    <p>{`If you are `}<a parentName="p" {...{
        "href": "https://styled-components.com/docs/basics#adapting-based-on-props"
      }}>{`adapting the styles based on props`}</a>{`, and those props are not part of the base tag / component props, you can tell TypeScript what those extra custom props are, with type arguments like this (`}<a parentName="p" {...{
        "href": "https://github.com/Microsoft/TypeScript/wiki/What%27s-new-in-TypeScript#generic-type-arguments-in-generic-tagged-templates"
      }}>{`TypeScript `}<inlineCode parentName="a">{`v2.9+`}</inlineCode>{` is required`}</a>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import styled from 'styled-components';
import Header from './Header';

interface TitleProps {
  readonly isActive: boolean;
}

const Title = styled.h1<TitleProps>\`
  color: \${(props) => (props.isActive ? props.theme.colors.main : props.theme.colors.secondary)};
\`;
`}</code></pre>
    <p>{`Note: if you style a standard tag (like `}<inlineCode parentName="p">{`<h1>`}</inlineCode>{` in above example), styled-components `}<a parentName="p" {...{
        "href": "https://styled-components.com/docs/basics#passed-props"
      }}>{`will not pass the custom props`}</a>{` (to avoid the `}<a parentName="p" {...{
        "href": "https://reactjs.org/warnings/unknown-prop.html"
      }}>{`Unknown Prop Warning`}</a>{`).`}</p>
    <p>{`However, it will pass all of them to a custom React component:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import styled from 'styled-components';
import Header from './Header';

const NewHeader = styled(Header)<{ customColor: string }>\`
  color: \${(props) => props.customColor};
\`;
// Header will also receive props.customColor
`}</code></pre>
    <p>{`If the `}<strong parentName="p">{`customColor`}</strong>{` property should not be transferred to the `}<strong parentName="p">{`Header`}</strong>{` component, you can leverage `}<a parentName="p" {...{
        "href": "https://styled-components.com/docs/api#transient-props"
      }}>{`transient props`}</a>{`, by prefixing it with a dollar sign ($):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import styled from 'styled-components';
import Header from './Header';

const NewHeader2 = styled(Header)<{ $customColor: string }>\`
  color: \${(props) => props.$customColor};
\`;
// Header does NOT receive props.$customColor
`}</code></pre>
    <p>{`Depending on your use case, you can achieve a similar result by extracting the custom props yourself:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import styled from 'styled-components';
import Header, { Props as HeaderProps } from './Header';

const NewHeader3 = styled(({ customColor, ...rest }: { customColor: string } & HeaderProps) => <Header {...rest} />)\`
  color: \${(props) => props.customColor};
\`;
`}</code></pre>
    <p>{`Or using `}<a parentName="p" {...{
        "href": "https://styled-components.com/docs/api#shouldforwardprop"
      }}>{`shouldForwardProp`}</a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import styled from 'styled-components';
import Header from './Header';

const NewHeader4 = styled(Header).withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) => !['customColor'].includes(prop),
})<{ customColor: string }>\`
  color: \${(props) => props.customColor};
\`;
`}</code></pre>
    <h3>{`Caveat with `}<inlineCode parentName="h3">{`className`}</inlineCode></h3>
    <p>{`When defining a component you will need to mark `}<inlineCode parentName="p">{`className`}</inlineCode>{` as optional
in your Props interface:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`interface LogoProps {
  /* This prop is optional, since TypeScript won't know that it's passed by the wrapper */
  className?: string;
}

class Logo extends React.Component<LogoProps, {}> {
  render() {
    return <div className={this.props.className}>Logo</div>;
  }
}

const LogoStyled = styled(Logo)\`
  font-family: 'Helvetica';
  font-weight: bold;
  font-size: 1.8rem;
\`;
`}</code></pre>
    <h3>{`Caveat with Function Components`}</h3>
    <p>{`To use function components and have typechecking for the props you'll need to define
the component alongside with its type. This is not special to styled-components, this is just
how React works:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`interface BoxProps {
  theme?: ThemeInterface;
  borders?: boolean;
  className?: string;
}

const Box: React.FunctionComponent<BoxProps> = (props) => <div className={props.className}>{props.children}</div>;

const StyledBox = styled(Box)\`
  padding: \${(props) => props.theme.lateralPadding};
\`;
`}</code></pre>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;